import React, { useContext, useEffect } from "react";
import OutgoingTransfer from "./OutgoingTransfer";
import IncomingTransfer from "./IncomingTransfer";
import { yearMonthDate } from "../../../utils/utils";
import { useNavigate } from "react-router-dom";
import { DataContext } from "../../../dataContext";
import { ROUTE_CONSTANTS } from "../../../constants/routeConstants";

const { INCOMING_DETAILS, OUTGOING_DETAILS } = ROUTE_CONSTANTS;

const History = () => {
  const navigate = useNavigate();
  const { history, getHistory, historyLoading, historyPagination, getNotificationsFeed } =
    useContext(DataContext);

  useEffect(() => {
    getHistory();
    getNotificationsFeed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let isLoading = false;
    
    const handleScroll = () => {
      // Check if we're at the bottom of the page
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = window.innerHeight;
      
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 100;
      
      if (isAtBottom && historyPagination.hasMore && !historyPagination.loadingMore && !isLoading) {
        isLoading = true;
        getHistory(true);
        // Reset flag after a delay to prevent rapid-fire requests
        setTimeout(() => {
          isLoading = false;
        }, 1000);
      }
    };

    // Add debouncing to scroll event
    let timeoutId;
    const debouncedHandleScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleScroll, 150);
    };

    window.addEventListener('scroll', debouncedHandleScroll);
    return () => {
      window.removeEventListener('scroll', debouncedHandleScroll);
      clearTimeout(timeoutId);
    };
  }, [historyPagination.hasMore, historyPagination.loadingMore, getHistory]);

  const transfersByDay = () => {
    const days = new Map();

    // Guard clause to prevent sorting undefined/null history
    if (!history || !Array.isArray(history)) {
      return days;
    }

    // History now includes the requester's own Request-for-Pay rows in every
    // state — pending (PENDING), as well as terminal declined/expired/cancelled
    // — alongside completed money movements. Pending requests render via
    // OutgoingTransfer (outgoing=true) and can be cancelled from the detail view.
    const filtered = [...history];

    filtered.sort((a, b) => {
      if (a.timestamp > b.timestamp) {
        return -1;
      }
      if (a.timestamp < b.timestamp) {
        return 1;
      }
      return 0;
    });

    filtered.forEach((transfer) => {
      const day = yearMonthDate(new Date(transfer.timestamp + "Z"));
      if (!days.has(day)) {
        days.set(day, []);
      }
      days.get(day).push({ type: "transfer", notification: transfer });
    });

    // sort transfers within each day
    days.forEach((dayTransfers) => {
      dayTransfers.sort((a, b) => {
        if (a.notification.timestamp > b.notification.timestamp) {
          return -1;
        }
        if (a.notification.timestamp < b.notification.timestamp) {
          return 1;
        }
        return 0;
      });
    });

    return days;
  };

  let sortedTransactions = history !== null ? Array.from(transfersByDay()) : undefined;

  // Early return if history is null
  if (!history) {
    return <>
      <main className="page_with_nav">
        <h3 className="center_page_with_nav">
          {historyLoading ? (
            <>Transfer history is loading.</>
          ) : (
            <>Something went wrong while fetching transfer history.</>
          )}
        </h3>
      </main>
    </>
  }

  return (
    <main className="page_with_nav">
      {historyLoading ? (
        <h3 className="center_page_with_nav">
          Transfer history is loading.
        </h3>
      ) : sortedTransactions && sortedTransactions.length > 0 ? (
        <ul>
          {sortedTransactions.map(([day, transactions], i) => {
            return (
              <div key={day}>
                <h5
                  style={
                    i === 0
                      ? { textAlign: "center", marginBottom: "10px" }
                      : { textAlign: "center", margin: "13px 0 10px 0" }
                  }
                >
                  {day.slice(5)}
                </h5>
                <ul>
                  {transactions.map((transaction, j) => {
                    const transfer = transaction.notification;
                    switch (transfer.outgoing) {
                      case false: {
                        return (
                          <IncomingTransfer
                            incoming={transfer}
                            key={j}
                            handleLinkClick={() =>
                              navigate(
                                `${INCOMING_DETAILS.replace(":id", transfer.id)}`
                              )
                            }
                          />
                        );
                      }
                      default: {
                        return (
                          <OutgoingTransfer
                            outgoing={transfer}
                            key={j}
                            handleLinkClick={() =>
                              navigate(
                                `${OUTGOING_DETAILS.replace(":id", transfer.id).replace(":action", "view")}`
                              )
                            }
                            isScheduled={!transfer.outgoing}
                          />
                        );
                      }
                    }
                  })}
                  {i !== sortedTransactions.length - 1 && (
                    <hr style={{ margin: "10px 0" }} />
                  )}
                </ul>
              </div>
            );
          })}
          {historyPagination.loadingMore && (
            <h3 style={{ textAlign: "center", margin: "20px 0" }}>
              Loading more transfers...
            </h3>
          )}
        </ul>
      )
        : <h3 className="center_page_with_nav">
          You have no transfer history at this time.
        </h3>
      }
    </main>
  );
};

export default History;
